import { BooLogo } from "@/components/BooLogo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { useSession } from "@/lib/auth-client";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { ArrowLeft, Building2, Coins, DollarSign, Flame, FlaskConical, Globe, Hash, HandCoins, Hourglass, KeyRound, Menu, MessageSquare, Server, Shield, ShieldAlert, Users, Zap } from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/superadmin/organizations", label: "Organizations", icon: Building2 },
  { to: "/superadmin/users", label: "Users", icon: Users },
  { to: "/superadmin/infrastructure", label: "Infrastructure", icon: Server },
  { to: "/superadmin/subdomains", label: "Subdomains", icon: Globe },
  { to: "/superadmin/named-warm-pool", label: "Named pool", icon: Flame },
  { to: "/superadmin/waitlist", label: "Waitlist", icon: Hourglass },
  { to: "/superadmin/costs", label: "Costs", icon: DollarSign },
  { to: "/superadmin/billing-grants", label: "Credit grants", icon: HandCoins },
  { to: "/superadmin/billing-config", label: "Billing config", icon: Coins },
  { to: "/superadmin/llm-usage", label: "LLM Usage", icon: Zap },
  { to: "/superadmin/gchat-custom", label: "Custom GChat", icon: MessageSquare },
  { to: "/superadmin/slack", label: "Slack", icon: Hash },
  { to: "/superadmin/permissions", label: "Permissions", icon: Shield },
  { to: "/superadmin/agent-keys", label: "API keys", icon: KeyRound },
  { to: "/superadmin/experiments", label: "Experiments", icon: FlaskConical },
] as const;

// Shared nav body — rendered both in the desktop `<aside>` and inside the
// mobile `<Sheet>` drawer so there is a single source of truth for the links.
// `onNavigate` lets the mobile drawer close itself when a link is tapped.
function AdminNav({
  pathname,
  userName,
  onNavigate,
}: {
  pathname: string;
  userName: string;
  onNavigate?: () => void;
}) {
  return (
    <>
      <div className="flex items-center gap-2 px-4 pt-4 pb-2">
        <BooLogo className="size-6 shrink-0" />
        <span className="text-base font-semibold tracking-tight">Boo Super Admin</span>
      </div>
      <div className="px-4 pb-2">
        <Badge className="border-transparent bg-indigo-100 text-indigo-900 dark:bg-indigo-500/20 dark:text-indigo-200">
          <ShieldAlert />
          SUPER ADMIN
        </Badge>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {NAV.map((item) => {
          const isActive = pathname.startsWith(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              className={`flex items-center gap-2 rounded-md px-2.5 py-2 text-sm transition-colors ${
                isActive
                  ? "bg-indigo-100 font-medium text-indigo-900 dark:bg-indigo-500/20 dark:text-indigo-100"
                  : "text-sidebar-foreground/70 hover:bg-indigo-100/60 hover:text-indigo-900 dark:hover:bg-indigo-500/10 dark:hover:text-indigo-100"
              }`}
            >
              <item.icon className="size-4" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="border-t border-indigo-200/60 px-4 py-3 space-y-3 dark:border-indigo-500/20">
        <Link
          to="/"
          onClick={onNavigate}
          className="flex items-center gap-2 text-xs text-sidebar-foreground/60 hover:text-sidebar-foreground transition-colors"
        >
          <ArrowLeft className="size-3.5" />
          Back to dashboard
        </Link>
        {userName && (
          <p className="text-xs text-sidebar-foreground/40 truncate">{userName}</p>
        )}
      </div>
    </>
  );
}

export function AdminLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: session } = useSession();
  const userName = session?.user?.name ?? "";
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex h-svh flex-col overflow-hidden">
      {/* Persistent environment marker — makes it unmistakable that this is the
          internal Super Admin space, not a customer workspace. */}
      <div className="flex shrink-0 items-center justify-center gap-2 bg-indigo-600 px-4 py-1.5 text-xs font-semibold tracking-wide text-white dark:bg-indigo-500">
        <ShieldAlert className="size-3.5" />
        SUPER ADMIN — internal · Boo Lab only
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Desktop sidebar — hidden on mobile, where the drawer takes over. */}
        <aside className="hidden w-56 shrink-0 flex-col border-r border-indigo-200/60 bg-indigo-50/50 md:flex dark:border-indigo-500/20 dark:bg-indigo-500/5">
          <AdminNav pathname={pathname} userName={userName} />
        </aside>

        {/* Mobile drawer — opened by the hamburger in the mobile top bar. */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent
            side="left"
            className="flex w-72 flex-col gap-0 border-indigo-200/60 bg-indigo-50 p-0 md:hidden dark:border-indigo-500/20 dark:bg-indigo-950"
          >
            <SheetTitle className="sr-only">Super Admin navigation</SheetTitle>
            <SheetDescription className="sr-only">
              Links to the Super Admin sections.
            </SheetDescription>
            <AdminNav
              pathname={pathname}
              userName={userName}
              onNavigate={() => setMobileOpen(false)}
            />
          </SheetContent>
        </Sheet>

        <div className="flex min-w-0 flex-1 flex-col">
          {/* Mobile top bar — hamburger to open the drawer + section marker. */}
          <header className="flex h-12 shrink-0 items-center gap-2 border-b border-indigo-200/60 px-3 md:hidden dark:border-indigo-500/20">
            <Button
              variant="ghost"
              size="icon-sm"
              className="-ml-1"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation"
            >
              <Menu />
            </Button>
            <BooLogo className="size-5 shrink-0" />
            <span className="truncate text-sm font-semibold">Boo Super Admin</span>
          </header>

          <main className="min-h-0 flex-1 overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
